import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CORE_PROPERTIES_TAB_IDS, usePropertiesTabs } from "contexts/propertiesTabs";
import { BaseWindowProps } from "contexts/windows/reducer";
import { useObjectData } from "hooks/useObjectData";
import { selectIsOnline } from "slices/explorer";

import type { WindowSearchProps, WindowTabsProps } from "../../types";
import { Window } from "../../window";
import { useBulkActionsContextMenu } from "../hooks/useBulkActionsContextMenu";

/**
 * Properties Window Component
 *
 * Displays object properties with search and tabbed interface.
 * Uses the new window infrastructure system.
 */
export function PropertiesWindow({ onClose, position = "center", ...props }: BaseWindowProps) {
    const { t } = useTranslation();
    const { object, hasAccess, isLoading } = useObjectData();
    const isOnline = useAppSelector(selectIsOnline);
    const {
        state: { view },
    } = useExplorerGlobals();
    const customTabs = usePropertiesTabs();
    const [searchTerm, setSearchTerm] = useState("");

    const { openContextMenu, contextMenuNode } = useBulkActionsContextMenu({
        origin: EventOrigin.PropertiesPanel,
    });

    const isEditableContextMenuTarget = (target: EventTarget | null) => {
        if (!(target instanceof Element)) {
            return false;
        }

        return Boolean(target.closest("input, textarea, [contenteditable='true'], [contenteditable='']"));
    };

    const handleContextMenu = (event: React.MouseEvent) => {
        if (isEditableContextMenuTarget(event.target)) {
            return;
        }

        event.preventDefault();
        openContextMenu(event.clientX, event.clientY);
    };

    const [activeTab, setActiveTab] = useState<string>(() => {
        return Object.keys(customTabs.tabEntries)[0] || CORE_PROPERTIES_TAB_IDS.PROPERTIES;
    });

    const [searchEmptyState, setSearchEmptyState] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            void track(Events.propertyPanel.searched, { search_term: trimmed });
        }, 500);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
        };
    }, [searchTerm]);
    const isSearchActive = searchTerm.trim().length > 0;

    const error = object && !hasAccess ? t("objectAccessForbidden") : null;

    const objectName = object?.base.find(([key]) => key === "Name")?.[1] || (object ? `Object ${object.id}` : "");

    const getEmptyState = () => {
        if (!object) {
            // Property data relies on `view.data`. While offline without it,
            // properties can't be loaded at all, so surface a clear offline
            // message instead of the generic "select an object" prompt.
            if (!isOnline && !view?.data) {
                return {
                    title: t("propertiesUnavailableOffline"),
                    message: t("propertiesUnavailableOfflineMessage"),
                    showIcon: false,
                };
            }

            return {
                title: t("selectAnObject"),
                message: t("selectObjectForProperties"),
            };
        }

        if (activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && isSearchActive && searchEmptyState) {
            return {
                title: t("noPropertiesMatchSearch"),
                message: t("pleaseRetrySearch"),
            };
        }

        return undefined;
    };

    const tabs: WindowTabsProps = useMemo(
        () => ({
            "data-testid": "properties-tabs",
            activeId: activeTab,
            onChange: setActiveTab,
            items: Object.values(customTabs.tabEntries).map((tabEntry) => ({
                id: tabEntry.config.id,
                label:
                    tabEntry.config.id === CORE_PROPERTIES_TAB_IDS.PROPERTIES ? t("properties") : tabEntry.config.label,
                "data-testid": `properties-tab-${tabEntry.config.id}`,
            })),
        }),
        [customTabs.tabEntries, activeTab, t],
    );

    const search: WindowSearchProps | undefined =
        activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES
            ? { value: searchTerm, onChange: setSearchTerm, placeholder: t("searchProperties") }
            : undefined;

    const renderActiveTab = () => {
        const tabEntry = customTabs.tabEntries[activeTab];
        if (!tabEntry) {
            return <div data-testid="properties-tab-not-found">Tab not found</div>;
        }

        const TabComponent = tabEntry.config.component;

        return (
            <div data-testid={`properties-tab-content-${activeTab}`}>
                <TabComponent
                    object={object}
                    hasAccess={hasAccess}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    onEmptyStateChange={setSearchEmptyState}
                />
            </div>
        );
    };

    return (
        <Window
            position={position}
            size={{ width: 400, height: 500 }}
            loading={isLoading}
            loadingMessage={t("loadingProperties")}
            error={error}
            emptyState={getEmptyState()}
            title={t("properties")}
            subheader={objectName || t("noObjectSelected")}
            tabs={tabs}
            search={search}
            onClose={onClose}
            secondaryBgColor
            data-testid="properties-window"
            {...props}
        >
            <div onContextMenu={handleContextMenu}>{renderActiveTab()}</div>
            {contextMenuNode}
        </Window>
    );
}
